import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  // Types et priorités (constantes métier conservées)
  const notificationTypes = {
    COURSE_UPDATE:         'course_update',
    NEW_LESSON:            'new_lesson',
    ASSIGNMENT_DUE:        'assignment_due',
    GRADE_AVAILABLE:       'grade_available',
    CERTIFICATE_READY:     'certificate_ready',
    CHAT_MESSAGE:          'chat_message',
    SYSTEM_ANNOUNCEMENT:   'system_announcement',
    PAYMENT_SUCCESS:       'payment_success',
    SUBSCRIPTION_EXPIRY:   'subscription_expiry',
    COURSE_RECOMMENDATION: 'course_recommendation',
  };

  const notificationPriorities = {
    LOW:    'low',
    MEDIUM: 'medium',
    HIGH:   'high',
    URGENT: 'urgent',
  };

  // ── Chargement depuis l'API ────────────────────────────────────────────────

  const loadNotifications = useCallback(async ({ unreadOnly = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (unreadOnly) params.unread_only = true;
      const { data } = await axios.get('/api/notifications', { params });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount ?? (data.notifications || []).filter(n => !n.is_read).length);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) loadNotifications(); }, [token, loadNotifications]);

  // ── Actions API ────────────────────────────────────────────────────────────

  const markAsRead = async (notificationId) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await axios.patch(`/api/notifications/${notificationId}/read`);
    } catch (err) {
      // Rollback en cas d'erreur
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
  };

  const markAllAsRead = async () => {
    const prevNotifications = notifications;
    const prevCount = unreadCount;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);

    try {
      await axios.patch('/api/notifications/read-all');
    } catch (err) {
      setNotifications(prevNotifications);
      setUnreadCount(prevCount);
    }
  };

  // Suppression locale uniquement (pas d'endpoint DELETE côté backend)
  const deleteNotification = (notificationId) => {
    const notif = notifications.find(n => n.id === notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notif && !notif.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // Création via API (admin) — les autres utilisateurs n'y ont pas accès
  const createNotification = async (notificationData) => {
    try {
      const { data } = await axios.post('/api/notifications', notificationData);
      setNotifications(prev => [data.notification, ...prev]);
      if (!data.notification.is_read) setUnreadCount(prev => prev + 1);
      return { success: true, notification: data.notification };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  };

  // ── Notifications push (API navigateur) ───────────────────────────────────

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) return false;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      return perm === 'granted';
    } catch {
      return false;
    }
  };

  const sendPushNotification = (notification) => {
    if (permission !== 'granted') return false;
    try {
      const push = new Notification(notification.title, {
        body:  notification.message || notification.body,
        icon:  '/favicon.ico',
        tag:   String(notification.id),
        data:  { url: notification.action_url || notification.actionUrl },
      });
      push.onclick = () => {
        window.focus();
        if (push.data?.url) window.location.href = push.data.url;
        push.close();
      };
      return true;
    } catch {
      return false;
    }
  };

  // ── Utilitaires de filtrage (opèrent sur l'état local) ────────────────────

  const getUnreadNotifications    = () => notifications.filter(n => !n.is_read);
  const getNotificationsByType    = (type) => notifications.filter(n => n.type === type);
  const getNotificationsByPriority = (priority) => notifications.filter(n => n.priority === priority);
  const getRecentNotifications    = () => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return notifications.filter(n => new Date(n.created_at || n.timestamp) > oneDayAgo);
  };

  const filterNotifications = (filters = {}) => {
    let list = [...notifications];
    if (filters.type)              list = list.filter(n => n.type === filters.type);
    if (filters.priority)          list = list.filter(n => n.priority === filters.priority);
    if (filters.read !== undefined) list = list.filter(n => n.is_read === filters.read);
    if (filters.category)          list = list.filter(n => n.category === filters.category);
    if (filters.courseId)          list = list.filter(n => n.course_id === filters.courseId || n.courseId === filters.courseId);
    return list;
  };

  const getNotificationStats = () => {
    const total  = notifications.length;
    const unread = notifications.filter(n => !n.is_read).length;
    const byType = {};
    const byPriority = {};
    notifications.forEach(n => {
      byType[n.type]         = (byType[n.type] || 0) + 1;
      byPriority[n.priority] = (byPriority[n.priority] || 0) + 1;
    });
    return { total, unread, read: total - unread, byType, byPriority };
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    permission,
    notificationTypes,
    notificationPriorities,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    requestNotificationPermission,
    sendPushNotification,
    getUnreadNotifications,
    getNotificationsByType,
    getNotificationsByPriority,
    getRecentNotifications,
    filterNotifications,
    getNotificationStats,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
